import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RoomTypes } from '@weblite/ts-action/room'
import { CallType } from '@weblite/ts-action/media-proxy'
import { nothing } from '$func/utils'
import { RootState } from '$setup/redux'
import {
  callEndedAudio,
  incomingCallAudio,
  outgoingCallAudio,
} from '$setup/audios'
import { useTimeout } from '$hooks/use-timeout'
import { showErrorSnackbar } from '$comp/snackbars'
import { t } from '$i18n'
import { callService } from '$logic/call/service'
import { useLastCallback } from '$hooks/use-last-callback'
import { CallState } from './types'
import { CallStorage } from './storage'

const callStorageSelector = (rootState: RootState) =>
  rootState.main.call as CallStorage

export function useCallStorage() {
  return useSelector(callStorageSelector)
}

export function useShowCallBar() {
  const { roomId, state } = useCallStorage()
  return Boolean(roomId) && state !== 'ended'
}

export function useHandleCallSounds() {
  const oldStateRef = useRef<CallState>()
  const { state, status } = useCallStorage()

  useEffect(() => {
    if (state === oldStateRef.current) return
    if (!oldStateRef.current) oldStateRef.current = state

    // state !== oldState
    oldStateRef.current = state
    outgoingCallAudio.pause()
    incomingCallAudio.pause()

    if (state === 'ended' && status !== 'successful')
      callEndedAudio.play()?.catch(nothing)
    else if (state === 'outgoing') outgoingCallAudio.play()?.catch(nothing)
    else if (state === 'incoming') incomingCallAudio.play()?.catch(nothing)
  }, [state, status])
}

const JOIN_CALL_TIMEOUT = 5000
export function useJoinCallWithTimeout(timeoutMs = JOIN_CALL_TIMEOUT) {
  const [joiningCall, setJoiningCall] = useState(false)
  const { create: createTimeout, clear: cancelTimeout } = useTimeout()

  const joinCall = useLastCallback(
    (roomId: string, roomType: RoomTypes, callType: CallType) => {
      createTimeout(() => {
        setJoiningCall(false)
        showErrorSnackbar(t('snackbar.call.joinFailed')(t(`call.${callType}`)))
      }, timeoutMs)

      setJoiningCall(true)
      callService.joinCall(roomId, roomType, callType).finally(() => {
        setJoiningCall(false)
        cancelTimeout()
      })
    },
  )

  return { joiningCall, joinCall }
}
